'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import {
  DISPONIBILIDAD_OPTIONS,
  ESPECIALIZACION_OPTIONS,
  type TalleristaApplicationPayload,
  talleristaApplicationSchema,
} from '@impacta/shared/schemas/forms';
import { CheckboxField } from '@/components/forms/CheckboxField';
import { FormError } from '@/components/forms/FormError';
import { FormSuccessCard } from '@/components/forms/FormSuccessCard';
import { RadioGroupField } from '@/components/forms/RadioGroupField';
import { SelectField } from '@/components/forms/SelectField';
import { SubmitButton } from '@/components/forms/SubmitButton';
import { TextAreaField } from '@/components/forms/TextAreaField';
import { TextField } from '@/components/forms/TextField';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/forms/TurnstileWidget';
import { errorMessage } from '@/lib/forms/error-messages';
import { submitForm } from '@/lib/forms/submit';
import { FEATURE_AUTH_ENABLED } from '@/lib/features';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const ESPECIALIZACION_SELECT = ESPECIALIZACION_OPTIONS.map((v) => ({ value: v, label: v }));
const DISPONIBILIDAD_SELECT = DISPONIBILIDAD_OPTIONS.map((v) => ({ value: v, label: v }));

const EXPERIENCIA_MIN = 50;
const FILOSOFIA_MIN = 80;

export default function PostularForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<TalleristaApplicationPayload>({
    mode: 'onChange',
    resolver: yupResolver(
      talleristaApplicationSchema
    ) as unknown as Resolver<TalleristaApplicationPayload>,
    defaultValues: {
      tieneCuenta: false,
      email: '',
      nombre: '',
      especializacion: '' as unknown as TalleristaApplicationPayload['especializacion'],
      experiencia: '',
      portfolioUrl: '',
      disponibilidad: '' as unknown as TalleristaApplicationPayload['disponibilidad'],
      filosofia: '',
      aceptaTerminos: false,
    },
  });

  const tieneCuenta = watch('tieneCuenta');
  const isNewApplicant = tieneCuenta === false;
  const experienciaValue = watch('experiencia') ?? '';
  const filosofiaValue = watch('filosofia') ?? '';

  if (status === 'success') {
    return (
      <FormSuccessCard
        heading="¡Postulación enviada!"
        body="Recibimos tu postulación. Si hay un encaje con nuestra programación, te contactaremos en 7–10 días hábiles."
        linkHref="/actividades"
        linkLabel="Explorar actividades"
      />
    );
  }

  async function onValid(data: TalleristaApplicationPayload) {
    if (!token) return;
    setStatus('submitting');
    setErrorBanner(null);

    const result = await submitForm({
      endpoint: '/api/forms/applications',
      payload: data,
      token,
    });

    if (result.ok) {
      reset();
      setToken(null);
      setStatus('success');
    } else {
      setErrorBanner(errorMessage(result.errorCode, result.detail));
      turnstileRef.current?.reset();
      setStatus('error');
      console.error('submit-failed', { errorCode: result.errorCode });
    }
  }

  function onInvalid() {
    const first = Object.keys(errors)[0] as keyof TalleristaApplicationPayload | undefined;
    if (first) setFocus(first);
  }

  return (
    <form
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
      className="bg-white rounded-2xl shadow-sm border border-[#E8E8E4] p-8 space-y-5"
    >
      {FEATURE_AUTH_ENABLED && (
        <RadioGroupField
          name="tieneCuenta"
          label="¿Ya tienes cuenta en IMPACTA.red?"
          required
          options={[
            { value: 'true', label: 'Sí, ya tengo cuenta' },
            { value: 'false', label: 'No, soy nuevo aquí' },
          ]}
          register={register}
          error={errors.tieneCuenta?.message}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField
          name="email"
          type="email"
          label="Correo electrónico"
          placeholder="tu@correo.cl"
          required
          register={register}
          error={errors.email?.message}
        />
        <TextField
          name="nombre"
          label="Nombre completo"
          placeholder="Tu nombre"
          required={isNewApplicant}
          helperText={isNewApplicant ? undefined : 'Solo necesario si no tienes cuenta'}
          register={register}
          error={errors.nombre?.message}
        />
      </div>

      <SelectField
        name="especializacion"
        label="Especialización"
        options={ESPECIALIZACION_SELECT}
        placeholder="Selecciona una especialización"
        required
        register={register}
        error={errors.especializacion?.message}
      />

      <div>
        <TextAreaField
          name="experiencia"
          label="Cuéntanos sobre tu experiencia"
          placeholder="Mínimo 50 caracteres. Cuéntanos sobre tu trayectoria, actividades impartidos, formación..."
          required
          rows={4}
          register={register}
          error={errors.experiencia?.message}
        />
        <p
          className={`mt-1 text-xs ${
            experienciaValue.length >= EXPERIENCIA_MIN ? 'text-[#00A19B]' : 'text-[#6b6e68]'
          }`}
        >
          {experienciaValue.length} / {EXPERIENCIA_MIN} caracteres mínimo
        </p>
      </div>

      <TextField
        name="portfolioUrl"
        type="url"
        label="Portfolio o redes (opcional)"
        placeholder="https://www.instagram.com/..."
        register={register}
        error={errors.portfolioUrl?.message}
      />

      <SelectField
        name="disponibilidad"
        label="Disponibilidad horaria"
        options={DISPONIBILIDAD_SELECT}
        placeholder="Selecciona tu disponibilidad"
        required
        register={register}
        error={errors.disponibilidad?.message}
      />

      <div>
        <TextAreaField
          name="filosofia"
          label="Filosofía de enseñanza"
          placeholder="Mínimo 80 caracteres. ¿Qué buscas transmitir? ¿Cómo es tu metodología?"
          required
          rows={5}
          register={register}
          error={errors.filosofia?.message}
        />
        <p
          className={`mt-1 text-xs ${
            filosofiaValue.length >= FILOSOFIA_MIN ? 'text-[#00A19B]' : 'text-[#6b6e68]'
          }`}
        >
          {filosofiaValue.length} / {FILOSOFIA_MIN} caracteres mínimo
        </p>
      </div>

      <CheckboxField
        name="aceptaTerminos"
        required
        register={register}
        error={errors.aceptaTerminos?.message}
      >
        Acepto los{' '}
        <Link href="/terminos" className="text-[#00A19B] underline hover:no-underline">
          términos
        </Link>{' '}
        y la{' '}
        <Link href="/privacidad" className="text-[#00A19B] underline hover:no-underline">
          política de privacidad
        </Link>
      </CheckboxField>

      <TurnstileWidget ref={turnstileRef} onToken={setToken} />

      {errorBanner && <FormError variant="banner" message={errorBanner} />}

      <SubmitButton
        variant="purple"
        submitting={status === 'submitting'}
        disabled={!token || !isValid || status === 'submitting' || isSubmitting}
        idleLabel="Enviar postulación"
        submittingLabel="Enviando..."
      />
    </form>
  );
}
